import React, { useState, useEffect, useCallback, useMemo } from "react";
import WordFrequencyChart from "../components/BarChartComponent";

export default function CharacterCounterPage() {
  const [myText, updateText] = useState("");
  const [spacesCount, updateSpacesCount] = useState(0);
  const [wordsMapList, updateWordsMap] = useState([]);
  const [doExcludeHelperWords, updateDoExcludeHelperWordsFlag] = useState(false);

  // Memoized set of helper words
  const stopWords = useMemo(
    () => new Set([
      "a", "an", "the", "is", "in", "on", "at", "to", "for", "of", "and", "or", "by", "with", "that",
      "this", "it", "as", "are", "was", "were", "be", "been", "but", "from", "which", "who", "whom",
      "its", "their", "there", "here", "about", "so", "if", "then", "else", "when", "while", "no"
    ]),
    []
  );

  // Remove helper words
  const removeHelperWords = useCallback(
    (text) => text.toLowerCase().split(/\s+/).filter(word => !stopWords.has(word)).join(" "),
    [stopWords]
  );

  // Count spaces
  const countSpaces = useCallback(() => {
    updateSpacesCount((myText.match(/\s/g) || []).length);
  }, [myText]);

  // Count word frequency
  const countWordFrequency = useCallback(() => {
    const text = doExcludeHelperWords ? removeHelperWords(myText) : myText;
    const wordMap = {};
    text.toLowerCase().replace(/[^\w\s]/g, "").split(/\s+/).forEach(word => {
      if (word) wordMap[word] = (wordMap[word] || 0) + 1;
    });
    updateWordsMap(Object.entries(wordMap).sort((a, b) => b[1] - a[1]));
  }, [myText, doExcludeHelperWords, removeHelperWords]);

  // Effects
  useEffect(() => {
    countSpaces();
    countWordFrequency();
  }, [myText, countSpaces, countWordFrequency]);

  useEffect(() => {
    countWordFrequency();
  }, [doExcludeHelperWords, countWordFrequency]);

  // Handlers
  const handleOnChangeText = (value) => updateText(value);
  const handleUpperCase = () => updateText(myText.toUpperCase());
  const handleLowerCase = () => updateText(myText.toLowerCase());
  const handleCamelCase = () => updateText(
    myText.trim().toLowerCase().split(/(\s+)/).map(segment =>
      segment.trim() === "" ? segment : segment.charAt(0).toUpperCase() + segment.slice(1)
    ).join("")
  );
  const capitalizeFirstLetter = () => {
    const updated = myText.toLowerCase().split(/([.!?]\s*)/g).map((segment, idx) =>
      idx % 2 === 0 ? segment.charAt(0).toUpperCase() + segment.slice(1) : segment
    ).join("");
    updateText(updated);
  };
  const handleCheckboxChange = () => updateDoExcludeHelperWordsFlag(prev => !prev);

  // Export CSV
  const downloadCSV = (data) => {
    const rows = [["Word", "Count"], ...data.map(([w, c]) => [w, c])];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "word_frequencies.csv"; a.click(); URL.revokeObjectURL(url);
  };

  // Counts
  const countSentences = () => myText.split(/(?<=[.!?])\s+/).filter(s => s.trim()).length;
  const countParagraphs = () => myText.split(/\n+/).filter(p => p.trim()).length;
  const wordCount = myText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="container py-4">
      <h2 className="text-center mb-4">🧮 Character Counter Tool</h2>

      <textarea
        value={myText}
        onChange={e => handleOnChangeText(e.target.value)}
        className="form-control mb-3"
        rows={6}
        placeholder="Start typing your text here..."
      />

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button className="btn btn-primary" onClick={handleUpperCase}>Uppercase</button>
        <button className="btn btn-secondary" onClick={handleLowerCase}>Lowercase</button>
        <button className="btn btn-info" onClick={handleCamelCase}>Camel Case</button>
        <button className="btn btn-warning" onClick={capitalizeFirstLetter}>Capitalize Sentences</button>
      </div>

      <div className="form-check form-switch mb-3">
        <input className="form-check-input" type="checkbox" checked={doExcludeHelperWords} onChange={handleCheckboxChange} id="excludeHelpers" />
        <label className="form-check-label" htmlFor="excludeHelpers">Exclude Helper Words</label>
      </div>

      <div className="row text-center mb-4">
        <div className="col-md-3"><div className="border p-2">Characters: <strong>{myText.length}</strong></div></div>
        <div className="col-md-3"><div className="border p-2">Words: <strong>{wordCount}</strong></div></div>
        <div className="col-md-3"><div className="border p-2">Sentences: <strong>{countSentences()}</strong></div></div>
        <div className="col-md-3"><div className="border p-2">Paragraphs: <strong>{countParagraphs()}</strong></div></div>
        <div className="col-md-3"><div className="border p-2">Spaces: <strong>{spacesCount}</strong></div></div>
      </div>

      <div className="mb-5">
        <h4>📊 Word Frequency</h4>
        <WordFrequencyChart data={wordsMapList} />
        <button className="btn btn-success mt-3" onClick={() => downloadCSV(wordsMapList)}>Export to CSV</button>
      </div>
    </div>
  );
}
